import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import AuthShell, { AuthLabel, AuthInput, AuthPrimaryButton, AuthErrorLine, formatApiErrorDetail } from "../components/AuthShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Post-sign-in profile completion for users (mostly Google) that don't have
 * a shipping address on record yet. Blocks checkout until this is filled.
 */
const CompleteProfile = () => {
  const { user, loading, setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [line1, setLine1] = useState("");
  const [line2, setLine2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [postal, setPostal] = useState("");
  const [country, setCountry] = useState("US");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    setName(user.name || "");
    setPhone(user.phone || "");
    const a = user.address || {};
    setLine1(a.line1 || ""); setLine2(a.line2 || "");
    setCity(a.city || ""); setState(a.state || "");
    setPostal(a.postal_code || ""); setCountry(a.country || "US");
    // If they already have a complete address AND phone, bounce them out
    if (a.line1 && a.city && a.state && a.postal_code && a.country && user.phone) {
      const dest = new URLSearchParams(location.search).get("next") === "cart" ? "/" : "/account";
      if (new URLSearchParams(location.search).get("next") === "cart") {
        try { sessionStorage.setItem("wiu_reopen_cart", "1"); } catch (_e) { /* ignore */ }
      }
      navigate(dest, { replace: true });
    }
  }, [user, loading, navigate, location.search]);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setSubmitting(true);
    try {
      const { data } = await axios.patch(`${API}/auth/profile`, {
        name: name || null,
        phone,
        address: { line1, line2: line2 || null, city, state, postal_code: postal, country },
      }, { withCredentials: true });
      setUser(data);
      const wantsCart = new URLSearchParams(location.search).get("next") === "cart";
      if (wantsCart) {
        try { sessionStorage.setItem("wiu_reopen_cart", "1"); } catch (_e) { /* ignore */ }
        navigate("/");
      } else {
        navigate("/account");
      }
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to update profile");
    } finally { setSubmitting(false); }
  };

  const complete = name && phone && line1 && city && state && postal && country;

  if (loading) {
    return (
      <main className="min-h-screen bg-charcoal flex items-center justify-center">
        <div className="flex items-center gap-3 text-off-white/60">
          <Loader2 size={20} className="animate-spin text-rose" />
          <span className="font-mono text-[11px] tracking-[0.25em]">LOADING…</span>
        </div>
      </main>
    );
  }

  return (
    <AuthShell
      eyebrow="ONE LAST STEP"
      title="Where do we ship?"
      subtitle="We just need your name and address to complete your account and process orders."
    >
      <form onSubmit={submit} className="mt-8 space-y-4" data-testid="complete-profile-form">
        <div>
          <AuthLabel>FULL NAME</AuthLabel>
          <AuthInput type="text" value={name} onChange={(e) => setName(e.target.value)} required data-testid="cp-name-input" />
        </div>
        <div>
          <AuthLabel>PHONE</AuthLabel>
          <AuthInput type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} required data-testid="cp-phone-input" />
        </div>
        <div>
          <AuthLabel>STREET ADDRESS</AuthLabel>
          <AuthInput type="text" value={line1} onChange={(e) => setLine1(e.target.value)} required data-testid="cp-line1-input" />
        </div>
        <div>
          <AuthLabel>APT / SUITE (OPTIONAL)</AuthLabel>
          <AuthInput type="text" value={line2} onChange={(e) => setLine2(e.target.value)} data-testid="cp-line2-input" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <AuthLabel>CITY</AuthLabel>
            <AuthInput type="text" value={city} onChange={(e) => setCity(e.target.value)} required data-testid="cp-city-input" />
          </div>
          <div>
            <AuthLabel>STATE</AuthLabel>
            <AuthInput type="text" value={state} onChange={(e) => setState(e.target.value)} required data-testid="cp-state-input" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <AuthLabel>POSTAL CODE</AuthLabel>
            <AuthInput type="text" value={postal} onChange={(e) => setPostal(e.target.value)} required data-testid="cp-postal-input" />
          </div>
          <div>
            <AuthLabel>COUNTRY</AuthLabel>
            <AuthInput type="text" value={country} onChange={(e) => setCountry(e.target.value)} required data-testid="cp-country-input" />
          </div>
        </div>
        <AuthErrorLine error={error} />
        <AuthPrimaryButton type="submit" loading={submitting} disabled={!complete} data-testid="cp-submit-btn">
          SAVE & CONTINUE <ArrowRight size={16} />
        </AuthPrimaryButton>
      </form>
    </AuthShell>
  );
};

export default CompleteProfile;
